import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Route, Routes, BrowserRouter, useNavigate } from "react-router-dom";

import { MsalProvider, useMsal } from "@azure/msal-react";
import { AuthenticationResult, CacheLookupPolicy, EventType, InteractionType, IPublicClientApplication } from "@azure/msal-browser";
import { ThemeProvider } from "@mui/material";
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';

import { b2cPolicies } from "./authConfig";
import { PageLayout } from "./components/core/PageLayout";
import { Hello } from "./pages/Hello";

import './App.scss';
import { wpTheme } from "./styles/mui-theme";
import { Home } from "./pages/Home/Home";
import { Forms } from "./pages/Forms/Forms";
import { Users } from "./pages/Users/Users";
import { WelcomeBoardSettings } from "./pages/WelcomeBoard/Settings/WelcomeBoardSettings";
import { WelcomeBoardLayout } from "./pages/WelcomeBoard/Layout/WelcomeBoardLayout";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DependencyInjectionProvider } from "./contexts/dependency-injection-context";
import { FormsApi, User } from "./common";
import { ApplicationState } from "./contexts/services/application-state";
import FormSubmissions from "./pages/Forms/FormSubmissions/FormSubmissions";
import FormSubmissionViewer from "./pages/Forms/FormSubmissions/FormSubmissionViewer/FormSubmissionViewer";
import { loginRequest } from "./authConfig";

const formsApi = new FormsApi(process.env.REACT_APP_API_URL ?? '', process.env.REACT_APP_CLIENT_ID ?? '', process.env.REACT_APP_API_KEY);
const appState = new ApplicationState(formsApi);

const Pages = () => {

  /**
   * useMsal is hook that returns the PublicClientApplication instance, 
   * an array of all accounts currently signed in and an inProgress value 
   * that tells you what msal is currently doing. For more, visit:
   * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/hooks.md
   */
  const { accounts, instance } = useMsal();
  const navigate = useNavigate();
  const [token, setToken] = useState<AuthenticationResult>();

  const tokenCallback = async (): Promise<string> => {
    return new Promise<string>((resolve, reject) => {
      const account = instance.getActiveAccount();

      if (!account) {
        throw Error("No active account! Verify a user has been signed in and setActiveAccount has been called.");
      }

      const request = {
        ...loginRequest,
        forceRefresh: false,
        cacheLookupPolicy: CacheLookupPolicy.Default,
        account: account ?? accounts[0]
      };

      var now = new Date();
      var expirationCheck = new Date(now.getTime() - 10 * 1000); // Set the expiration check to be 10 seconds in the past

      var expirationEpoch = account.idTokenClaims?.exp;
      var expiration = new Date(0);

      if (expirationEpoch != null) {
        expiration.setUTCSeconds(expirationEpoch);
      }

      // Default MSAL caching does not seem to be working here, so we're doing a manual check against the ID token's expiration

      if (!account.idToken || expiration <= expirationCheck) {

        instance.acquireTokenSilent(request).then((response) => {
          resolve(response.idToken);
        }, (error) => {
          instance.acquireTokenRedirect(request);
          // Redirect will cause page reload, so we don't resolve the promise here
        });
      }
      else {
        resolve(account.idToken);
      }
    });
  }

  formsApi.setTokenCallback(tokenCallback);

  /**
   * Using the event API, you can register an event callback that will do something when an event is emitted. 
   * When registering an event callback in a react component you will need to make sure you do 2 things.
   * 1) The callback is registered only once
   * 2) The callback is unregistered before the component unmounts.
   * For more, visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/events.md
   */
  useEffect(() => {
    const callbackId = instance.addEventCallback(async (event: any) => {
      const redirectUrl = localStorage.getItem("redirectUrl");
      const path = window.location.pathname;

      if (event.eventType === EventType.HANDLE_REDIRECT_START && redirectUrl == null && path !== "/") {
        localStorage.setItem("redirectUrl", window.location.pathname);
      }

      if (event.eventType === EventType.LOGIN_FAILURE) {
        if (event.error && event.error.errorMessage.indexOf("AADB2C90118") > -1) {
          if (event.interactionType === InteractionType.Redirect) {
            instance.loginRedirect({ ...b2cPolicies.authorities.forgotPassword, scopes: [] });
          } else if (event.interactionType === InteractionType.Popup) {
            instance.loginPopup({ ...b2cPolicies.authorities.forgotPassword, scopes: [] })
              .catch(e => {
                return;
              });
          }
        }
      }

      if (event.eventType === EventType.LOGIN_SUCCESS || event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) {
        if (redirectUrl) {
          navigate(redirectUrl);
          localStorage.removeItem("redirectUrl");
        }

        if (event?.payload) {
          /**
           * We need to reject id tokens that were not issued with the default sign-in policy.
           * "acr" claim in the token tells us what policy is used (NOTE: for new policies (v2.0), use "tfp" instead of "acr").
           * To learn more about B2C tokens, visit https://docs.microsoft.com/en-us/azure/active-directory-b2c/tokens-overview
           */
          if (event.payload.idTokenClaims["acr"] === b2cPolicies.names.forgotPassword) {
            window.alert("Password has been reset successfully. \nPlease sign-in with your new password.");
            return instance.logout();
          } else if (event.payload.idTokenClaims["acr"] === b2cPolicies.names.editProfile) {
            window.alert("Profile has been edited successfully. \nPlease sign-in again.");
            return instance.logout();
          }
        }
      }
    });

    return () => {
      if (callbackId) {
        instance.removeEventCallback(callbackId);
      }
    };
  }, []);


  return (
    <Routes>
      <Route path="/" element={<Home />}></Route>
      <Route path="/forms/definitions" element={<Forms />}></Route>
      <Route path="/forms/submissions" element={<FormSubmissions />}></Route>
      <Route path="/forms/submissions/:id" element={<FormSubmissionViewer />}></Route>
      <Route path="/users" element={<Users />}></Route>
      <Route path="/welcome-board" element={<WelcomeBoardSettings />}></Route>
      <Route path="/welcome-board/layout" element={<WelcomeBoardLayout />}></Route>
    </Routes>
  )
}

const App = ({ instance }: { instance: IPublicClientApplication }) => {
  // The next 3 lines are optional. This is how you configure MSAL to take advantage of the router's navigate functions when MSAL redirects between pages in your app
  // const navigate = useNavigate();
  // const navigationClient = new CustomNavigationClient(navigate);
  // instance.setNavigationClient(navigationClient);

  return (
    <DependencyInjectionProvider formsApi={formsApi} applicationState={appState}>
      <MsalProvider instance={instance}>
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <ThemeProvider theme={wpTheme}>
            <PageLayout>
              <Pages />
            </PageLayout>
          </ThemeProvider>
        </LocalizationProvider>
      </MsalProvider>
    </DependencyInjectionProvider>
  );
}

export default App;
